class SaMotionHighlights extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;

    this._connected = true;
    this.pin = this.querySelector('[data-sa-motion-pin]');
    this.viewport = this.querySelector('[data-sa-motion-viewport]');
    this.track = this.querySelector('[data-sa-motion-track]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.rafId = null;
    this.maxTravel = 0;

    if (!this.pin || !this.viewport || !this.track) return;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleMotionPreference = this.handleMotionPreference.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);

    this.dataset.enhanced = 'true';
    this.addEventListener('shopify:block:select', this.handleBlockSelect);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.reducedMotion.addEventListener?.('change', this.handleMotionPreference);

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.pin);
    this.resizeObserver.observe(this.viewport);
    this.resizeObserver.observe(this.track);

    document.fonts?.ready.then(() => this.measure());
    this.measure();
  }

  disconnectedCallback() {
    if (!this._connected) return;

    this._connected = false;
    this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    this.reducedMotion?.removeEventListener?.('change', this.handleMotionPreference);
    this.resizeObserver?.disconnect();

    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);
  }

  handleScroll() {
    if (this.rafId !== null) return;

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.update();
    });
  }

  handleResize() {
    if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);
    this.resizeRafId = requestAnimationFrame(() => {
      this.resizeRafId = null;
      this.measure();
    });
  }

  handleMotionPreference() {
    this.measure();
  }

  fitDesktopHeight() {
    const content = this.querySelector('.sa-motion-highlights__content');
    const header = this.querySelector('.sa-motion-highlights__header');
    const cards = [...this.querySelectorAll('[data-sa-motion-card]')];
    this.style.removeProperty('--sa-mh-fit-card-width');
    this.style.removeProperty('--sa-mh-fit-section-padding');
    delete this.dataset.heightFit;

    if (!window.matchMedia('(min-width: 1024px)').matches || !cards.length) return;

    const availableHeight = window.innerHeight;
    const requiredHeight = () => {
      const style = getComputedStyle(content);
      return parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
        + (header?.getBoundingClientRect().height || 0)
        + parseFloat(getComputedStyle(this.viewport).marginTop)
        + Math.max(...cards.map(card => card.getBoundingClientRect().height));
    };

    const overflowBeforeFit = requiredHeight() - availableHeight;
    if (overflowBeforeFit <= 0) return;

    // Preserve the Figma 120px inset at its reference size. When the viewport
    // gets shorter, spend the vertical padding budget before touching cards.
    const preferredPadding = parseFloat(getComputedStyle(content).paddingTop);
    this.style.setProperty('--sa-mh-fit-section-padding', 'var(--sa-mh-fit-padding-min)');
    const minimumPadding = parseFloat(getComputedStyle(content).paddingTop);
    const fittedPadding = Math.max(minimumPadding, preferredPadding - Math.ceil(overflowBeforeFit / 2));
    if (fittedPadding < preferredPadding) {
      this.style.setProperty('--sa-mh-fit-section-padding', `${fittedPadding}px`);
    } else {
      this.style.removeProperty('--sa-mh-fit-section-padding');
    }
    if (requiredHeight() <= availableHeight) return;

    // Next reduce the square cards, preserving their copy and the Figma gaps.
    const minWidth = parseFloat(getComputedStyle(this).getPropertyValue('--sa-mh-card-min-width'));
    const fitCards = () => {
      let width = cards[0].getBoundingClientRect().width;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        const overflow = requiredHeight() - availableHeight;
        if (overflow <= 0.5 || width <= minWidth) break;
        width = Math.max(minWidth, Math.floor(width - overflow));
        this.style.setProperty('--sa-mh-fit-card-width', `${width}px`);
      }
    };
    fitCards();
    if (requiredHeight() <= availableHeight) return;

    // At extreme zoom the card floor can still be too tall. Reclaim internal
    // spacing last, then fall back to native scrolling if it still will not fit.
    this.dataset.heightFit = 'true';
    this.style.removeProperty('--sa-mh-fit-card-width');
    fitCards();
  }

  measure() {
    if (!this.pin || !this.viewport || !this.track) return;

    const reduceMotion = this.reducedMotion.matches;
    this.fitDesktopHeight();
    this.maxTravel = Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);
    const contentHeight = this.querySelector('.sa-motion-highlights__content').getBoundingClientRect().height;
    // Navigation appears as an overlay and does not reserve component space.
    const availableHeight = window.innerHeight;
    const pinHeight = Math.max(availableHeight, contentHeight);
    this.pinOverflow = pinHeight - availableHeight;
    // At extreme zoom/long copy, keep ordinary vertical and native horizontal scrolling.
    const fitsDesktop = !window.matchMedia('(min-width: 1024px)').matches || contentHeight <= availableHeight + 1;
    const shouldScroll = !reduceMotion && this.maxTravel > 1 && fitsDesktop;

    this.dataset.scrollable = String(shouldScroll);
    this.viewport.tabIndex = shouldScroll ? -1 : 0;
    if (shouldScroll) {
      this.style.setProperty('--sa-mh-pin-height', `${pinHeight}px`);
      this.style.setProperty('--sa-mh-pin-overflow', `${this.pinOverflow}px`);
      this.style.height = `${pinHeight + this.maxTravel}px`;
    } else {
      this.style.removeProperty('--sa-mh-pin-height');
      this.style.removeProperty('--sa-mh-pin-overflow');
      this.style.height = 'auto';
    }
    this.update();
  }

  update() {
    if (this.dataset.scrollable !== 'true' || this.maxTravel <= 0) {
      this.style.setProperty('--sa-mh-translate-x', '0px');
      return;
    }

    const sectionTop = this.getBoundingClientRect().top;
    const distance = Math.min(this.maxTravel, Math.max(0, -sectionTop - this.pinOverflow));
    this.style.setProperty('--sa-mh-translate-x', `${-distance}px`);
  }

  handleBlockSelect(event) {
    if (this.dataset.scrollable !== 'true') return;

    const selectedCard = event.target.closest?.('[data-sa-motion-card]');
    const firstCard = this.track.querySelector('[data-sa-motion-card]');
    if (!selectedCard || !firstCard) return;

    const desiredTravel = Math.min(this.maxTravel, Math.max(0, selectedCard.offsetLeft - firstCard.offsetLeft));
    const rootTop = window.scrollY + this.getBoundingClientRect().top;
    window.scrollTo({
      top: rootTop + this.pinOverflow + desiredTravel,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('sa-motion-highlights')) {
  customElements.define('sa-motion-highlights', SaMotionHighlights);
}
